import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import useContactList from '@app/hooks/contacts/useContacts';
import type { Contact, ContactListParams } from '@app/services/api.types';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import { Flex, type TableColumnsType } from 'antd';
import React, { useState } from 'react';
import { CgSoftwareDownload } from 'react-icons/cg';
import { Link } from 'react-router';
import ContactFilters from './components/ContactFilters';
import ContactDrawer from './components/ContactDrawer';
import useContactActions from '@app/hooks/contacts/useContactsActions';

export const ContactsList = () => {
    const [selectedRecord, setSelectedRecord] = useState<Contact | null>(null);
    const [params, setParams] = useState<ContactListParams>({
        page: 1,
        per_page: 10,
        search: '',
        type_id: [],
        brand_id: [],
        location_id: [],
        order_by: undefined,
        order_dir: undefined,
    });

    // Fetch data from the API
    const { isLoading, data: contactList } = useContactList(params);
    const { exportContacts } = useContactActions();

    const columns: TableColumnsType<Contact> = [
        {
            title: 'Full Name',
            dataIndex: 'first_name',
            key: 'first_name',
            sorter: true,
            render: (_, record) =>
                [record?.first_name, record.last_name].filter(Boolean).join(' ') || '-',
        },
        {
            title: 'Most Recent Interaction',
            dataIndex: 'most_recent_interaction',
            key: 'most_recent_interaction',
            sorter: true,
            render: (value) => {
                return value ? fn.formatDate(value, 'DD/MM/YYYY') : '-';
            },
        },
        {
            title: 'Total Interactions',
            dataIndex: 'total_interactions',
            key: 'total_interactions',
            sorter: true,
            render: (value) => value || 0,
        },
        {
            title: 'Primary Phone',
            dataIndex: 'telephone',
            key: 'telephone',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Primary Email',
            dataIndex: 'email',
            key: 'email',
            sorter: true,
            render: (value) => value || '-',
        },
    ];

    const handleExport = () => {
        exportContacts.mutate(params);
    };

    return (
        <React.Fragment>
            <PageMeta title="Contacts" />

            <PageHeader
                breadcrumbItems={[
                    {
                        title: <Link to={url.adminPortal.dashboard}>Dashboard</Link>,
                    },
                    { title: 'Contacts' },
                ]}>
                <PageTitle title="Contacts" />
                <PageIntro>
                    <p>Manage customer contact information</p>
                </PageIntro>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content>
                    <Flex align="end" className="styled-table-header">
                        <ContactFilters
                            onChange={(values) => {
                                setParams((prev) => ({ ...prev, ...values, page: 1 }));
                            }}
                        />
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    icon={<CgSoftwareDownload size={20} />}
                                    disabled={Boolean(!contactList?.total)}
                                    loading={exportContacts.isPending}
                                    onClick={handleExport}>
                                    Export CSV
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    </Flex>

                    <br />

                    <StyledTable<Contact>
                        columns={columns}
                        dataSource={contactList?.data || []}
                        rowKey={({ id }) => id}
                        // scroll={{ x: 860 }}
                        loading={isLoading}
                        pagination={{
                            current: params.page,
                            pageSize: params.per_page,
                            total: contactList?.total || 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order_dir = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                per_page: pagination.pageSize,
                                order_by,
                                order_dir,
                            }));
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => setSelectedRecord(record),
                            };
                        }}
                        rowClassName={() => 'is-clickable'}
                    />

                    <ContactDrawer
                        size={700}
                        onClose={() => setSelectedRecord(null)}
                        open={Boolean(selectedRecord)}
                        data={selectedRecord}
                    />
                </PageLayout.Content>
            </PageLayout>
        </React.Fragment>
    );
};

export default ContactsList;
